import { useEffect, useRef } from "react";

import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import { type Planning, UserRole } from "@eisox/api/core-api";
import { Breadcrumb, Button, CanAccess, IconButton, Page, Plan, Sidebar } from "@eisox/design-system";
import { ArrowLeftIcon, MultisensorIcon, NutIcon, PanelLeftIcon, PointChartIcon, ValveIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";

import { roomApi } from "~/api";
import ExpandedRoom from "~/components/expended-room";
import Multisensor from "~/components/multisensor";
import Valve from "~/components/valve";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { MultisensorsDrawer, MultisensorTooltip } from "~/features/multisensor";
import {
  canAccessGeneralSettings,
  getValveRoomData,
  PlanningCard,
  Sensors,
  SetpointTemp,
  Settings,
} from "~/features/room";
import { ValveHistoryDialog, ValvesDrawer, ValveTooltip } from "~/features/valve";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { FloorProvider, useFloorContext } from "~/providers/floor.provider";
import { RoomProvider, useRoomContext } from "~/providers/room.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/floors_/$floorId_/rooms/$roomId")({
  loader: async ({ context: { queryClient }, params: { buildingId, floorId, roomId } }) => {
    const rooms = await queryClient.ensureQueryData(roomApi.queries.building(buildingId));
    const room = rooms.find(r => r.id === roomId);
    if (room && room.floor.id !== floorId) {
      throw redirect({
        to: "/buildings/$buildingId/floors/$floorId/rooms/$roomId",
        params: { buildingId, floorId: room.floor.id, roomId },
        replace: true,
      });
    }
    await Promise.all([
      queryClient.ensureQueryData(floorPlanQueries.byId(floorId)),
      queryClient.ensureQueryData(roomApi.queries.plannings(roomId)),
    ]);
  },
  head: async ({ match: { context }, params: { buildingId, roomId } }) => {
    const rooms = await context.queryClient.ensureQueryData(roomApi.queries.building(buildingId));
    return buildingPageHead(context.queryClient, buildingId, rooms.find(room => room.id === roomId)?.name ?? m.plan());
  },
  component: RouteComponent,
});

function Room({ plan, plannings }: { plan: File; plannings: Planning[] }) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isLaptop = useMediaQuery("(max-width: 992px)");

  const { permissions, building, boilerRooms, heatingNetworks, gateways, multisensors, valves, role } =
    useBuildingContext(Route.id);
  const { floor } = useFloorContext(Route.id);
  const { room, valves: roomValves, multisensors: roomMultisensors } = useRoomContext(Route.id);

  const planRef = useRef<ReactZoomPanPinchRef>(null);
  const itemRefs = useRef<Set<string>>(new Set());
  const hasZoomed = useRef(false);

  //* Visitors must not see the valves on the room plan
  const roomValvesDisplayed = role === UserRole.VISITOR ? [] : roomValves;

  const totalItemCount = roomValvesDisplayed.length + roomMultisensors.length;

  useEffect(() => {
    itemRefs.current = new Set();
    hasZoomed.current = false;
  }, [roomValves, roomMultisensors]);

  const elementRefCalback = (ref: HTMLDivElement | null) => {
    if (!ref || !planRef.current) return;
    itemRefs.current.add(ref.id);
    if (itemRefs.current.size === totalItemCount && !hasZoomed.current) {
      Plan.zoomToElements(planRef.current.instance, [...itemRefs.current]);
      hasZoomed.current = true;
    }
  };

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (!planRef.current || !hasZoomed.current) return;

      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        const instance = planRef.current?.instance;
        if (instance && itemRefs.current.size > 0) {
          Plan.zoomToElements(instance, [...itemRefs.current]);
        }
      }, 250);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [roomValves, roomMultisensors]);

  const canAccessValvesDrawer = role !== UserRole.VISITOR && !!permissions.valve?.read && valves.length > 0;
  const canAccessMultisensorsDrawer = !!permissions.multisensor?.read && multisensors.length > 0;

  return (
    <Page.Root>
      <Page.Header className="flex-nowrap *:shrink-0">
        {isMobile && (
          <>
            <Sidebar.Trigger asChild>
              <IconButton>
                <PanelLeftIcon />
              </IconButton>
            </Sidebar.Trigger>
            <IconButton asChild>
              <Link
                to={"/buildings/$buildingId/floors/$floorId"}
                params={{ buildingId: building.id, floorId: floor.id }}
              >
                <ArrowLeftIcon />
              </Link>
            </IconButton>
          </>
        )}
        {isMobile ? (
          <Page.Title className="min-w-0 shrink!">{room.name}</Page.Title>
        ) : (
          <Breadcrumb.Root className="min-w-0 shrink!">
            <Breadcrumb.List>
              <Breadcrumb.Item>
                <Breadcrumb.Link className="truncate" asChild>
                  <Link
                    to={"/buildings/$buildingId/floors/$floorId"}
                    params={{ buildingId: building.id, floorId: floor.id }}
                  >
                    {floor.name}
                  </Link>
                </Breadcrumb.Link>
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                <Breadcrumb.Page asChild>
                  <Page.Title className="select-auto">{room.name}</Page.Title>
                </Breadcrumb.Page>
              </Breadcrumb.Item>
            </Breadcrumb.List>
          </Breadcrumb.Root>
        )}
        <CanAccess isAuthorized={canAccessGeneralSettings(permissions)}>
          <Settings asChild>
            {isLaptop ? (
              <IconButton>
                <NutIcon />
              </IconButton>
            ) : (
              <Button>
                {m.settings()} <NutIcon />
              </Button>
            )}
          </Settings>
        </CanAccess>

        <CanAccess isAuthorized={canAccessValvesDrawer || canAccessMultisensorsDrawer}>
          <div className="gap-5 ml-auto flex items-center">
            <CanAccess isAuthorized={canAccessMultisensorsDrawer}>
              <MultisensorsDrawer
                building={building}
                multisensors={roomMultisensors}
                valves={valves}
                gateways={gateways}
                permissions={permissions}
              >
                {isLaptop ? (
                  <IconButton>
                    <MultisensorIcon />
                  </IconButton>
                ) : (
                  <Button>
                    {m.multisensors_list()} <MultisensorIcon />
                  </Button>
                )}
              </MultisensorsDrawer>
            </CanAccess>
            <CanAccess isAuthorized={canAccessValvesDrawer}>
              <ValvesDrawer
                valves={roomValves}
                building={building}
                boilerRooms={boilerRooms}
                heatingNetworks={heatingNetworks}
                permissions={permissions}
              >
                {isLaptop ? (
                  <IconButton>
                    <ValveIcon />
                  </IconButton>
                ) : (
                  <Button>
                    {m.valves_list()} <ValveIcon />
                  </Button>
                )}
              </ValvesDrawer>
            </CanAccess>
          </div>
        </CanAccess>
      </Page.Header>
      <Page.Content
        className={`gap-2.5 min-[1200px]:mt-0! min-[1200px]:mr-0! min-[1200px]:mb-10 min-[1200px]:ml-10 min-[1200px]:gap-5 md:gap-5 grid grid-cols-[1fr_1fr] grid-rows-[auto_auto_1fr] overflow-y-auto bg-transparent [grid-template-areas:'sensors_sensors'_'cards_cards'_'planning_planning'] min-[768px]:[grid-template-areas:'sensors_sensors'_'cards_cards'_'plan_plan'_'planning_planning'] min-[992px]:[grid-template-areas:'sensors_sensors'_'cards_planning'_'plan_planning'_'plan_planning'] min-[1200px]:grid-cols-[1fr_1fr_450px] min-[1200px]:rounded-l-[10px] min-[1200px]:[grid-template-areas:'sensors_sensors_planning'_'cards_cards_planning'_'plan_plan_planning']`}
        data-testid="Room"
      >
        <Sensors
          temperature={room.temperature}
          isTemperatureEstimated={room.isTemperatureEstimated}
          temperatureValveCount={room.temperatureValveCount}
          humidity={room.humidity}
          light={room.light}
          airQuality={room.airQuality}
          airQualityV2={room.airQualityV2}
          canReadHumidity={permissions.valve?.sensors?.humidity?.read}
          canReadAirQuality={permissions.valve?.sensors?.indoorAirQuality?.read}
          canReadLight={permissions.valve?.sensors?.light?.read}
          className="[grid-area:sensors]"
        />
        <div className="gap-2.5 flex [grid-area:cards]">
          <CanAccess isAuthorized={permissions.valve?.correctedTemp?.read && roomValves.length > 0}>
            <SetpointTemp
              className="flex-1"
              building={building}
              room={room}
              valves={roomValves}
              plannings={plannings}
              multisensors={roomMultisensors}
              heatingNetworks={heatingNetworks}
            />
          </CanAccess>
          <CanAccess isAuthorized={permissions.history?.valve.histoValve?.read && roomValves.some(v => !!v.mac)}>
            <ValveHistoryDialog buildingId={building.id} valves={roomValves}>
              <button
                type="button"
                className="gap-2.5 p-5 flex flex-1 cursor-pointer items-center justify-center rounded-[10px] bg-primary-white text-base [&_svg]:shrink-0"
              >
                {m.history()}
                <PointChartIcon />
              </button>
            </ValveHistoryDialog>
          </CanAccess>
        </div>
        <Plan.Root
          ref={planRef}
          wheel={{ disabled: true }}
          pinch={{ disabled: true }}
          doubleClick={{ disabled: true }}
          panning={{ disabled: true }}
        >
          <Plan.Wrapper
            plan={plan}
            className="md:block md:[grid-area:plan] hidden h-full! w-full! border-0 min-[1400px]:h-full!"
          >
            {roomValvesDisplayed.length > 0 || roomMultisensors.length > 0 ? (
              <>
                {roomValvesDisplayed.map(v => {
                  const roomData = getValveRoomData({ ...v.stateData, ...v.sensors });
                  return (
                    <ValveTooltip
                      key={v.id}
                      valve={v}
                      roomData={roomData}
                      building={building}
                      boilerRooms={boilerRooms}
                      heatingNetworks={heatingNetworks}
                      valves={valves}
                      permissions={permissions}
                    >
                      <Plan.Item
                        ref={e => elementRefCalback(e)}
                        id={v.id}
                        position={{ x: v.floor.x, y: v.floor.y }}
                        asChild
                      >
                        <Valve error={v.problemStatus} />
                      </Plan.Item>
                    </ValveTooltip>
                  );
                })}
                {roomMultisensors.map(ms => {
                  return (
                    <MultisensorTooltip key={ms.id} multisensor={ms} permissions={permissions}>
                      <Plan.Item
                        ref={e => elementRefCalback(e)}
                        id={ms.id}
                        position={{ x: ms.floor.x, y: ms.floor.y }}
                        asChild
                      >
                        <Multisensor error={ms.problemStatus} />
                      </Plan.Item>
                    </MultisensorTooltip>
                  );
                })}
              </>
            ) : (
              <Plan.Item position={{ x: room.floor.x, y: room.floor.y }} asChild>
                <ExpandedRoom selected>{room.name}</ExpandedRoom>
              </Plan.Item>
            )}
          </Plan.Wrapper>
        </Plan.Root>
        <PlanningCard
          className="w-full [grid-area:planning]"
          building={building}
          room={room}
          plannings={plannings}
          valves={valves}
          heatingNetworks={heatingNetworks}
          permissions={permissions}
        />
      </Page.Content>
    </Page.Root>
  );
}

function RouteComponent() {
  const { floorId, roomId } = Route.useParams();

  const { data: plan } = useSuspenseQuery({ ...floorPlanQueries.byId(floorId) });
  const { data: plannings } = useSuspenseQuery({ ...roomApi.queries.plannings(roomId) });

  return (
    <FloorProvider floorId={floorId}>
      <RoomProvider roomId={roomId}>
        <Room plan={plan} plannings={plannings} />
      </RoomProvider>
    </FloorProvider>
  );
}
